/* eslint-disable max-statements */
import React, { Fragment, useEffect, useState, useCallback } from "react";
import loadable from "@loadable/component";
import PropTypes from "prop-types";
import EventHandler from "../../utils/event-handler";
import { Helmet } from "react-helmet";
import { subscribe } from "valtio/vanilla";
import { proxyAuthState } from "../../service/userAuthApi";
import saveCookie from "../../utils/helpers/save-cookie";
import parseCookie from "../../utils/helpers/parse-cookie";
import { scheduleIdle } from "../../utils/helpers/yield-to-main";
import {
    LANDING_ROUTE_CONSTANTS,
    NUMBER,
    C2B_ROUTES,
    LOCAL_STORAGE_KEYS,
    SESSION_KEYS
} from "../../constants/app-constants";
import { ISLOGGEDIN_COOKIE, SUPPORT_WIDGET_HIDDEN_ROUTES } from "./constants.js";
import { emitEventNames } from "../../constants/event-constants";
import { loadClevertap } from "./dynamic-scripts";
import { useLocation } from "react-router";
import { getItem, setItem } from "../../utils/helpers/storage-crud";
import ErrorHandler from "../shared/with-error-handler";
import isWebView from "../../utils/helpers/is-web-view";
import { clevertapUserProperties, trackCommonCustomEventsAU } from "../../tracking/index.js";
import { getItemFromSession, setItemInSession } from "../../utils/helpers/session-storage.js";
import { PRE_BI_ASSISTANCE_CATEGORIES } from "../../config/au.mobile/pre-bi-assistance-config/index.js";

import { statsigInitialization } from "../../utils/statsig-ab-utils/initialize-user-csr";
import { EXPERIMENT_NAME } from "../../utils/statsig-ab-utils/constant.js";
import useExperiments from "../../hooks/use-statsig-experiment.js";
import { DEVICE_TYPE, getDeviceType } from "../../tracking/c2c-tracking-params.js";
import ANALYTICS_EVENTS from "../au.mobile/bi-to-phone-basic-details/tracking.js";
import { getAbExpirementVariant } from "../../utils/helpers/get-ab-expirement-variant.js";
import { EXPERIMENT_TYPE } from "../../constants/optimize-constants.js";
import useGoogleOneTap from "../../hooks/use-google-one-tap";
import useThirdPartySignIn from "../../hooks/use-third-party-signin";
import useBifrostSsoCallback from "../../hooks/use-bifrost-sso-callback";
const SupportWidgetDesktop = loadable(() => import("../au.desktop/support-widget"));
const SupportWidgetMobile = loadable(() => import("../au.mobile/support-widget"));
import useOnloadIdle from "../../hooks/use-onload-idle";
import parseLocationSearchParams from "../../utils/helpers/parse-location-search-params.js";
import isBifrostToken from "../../utils/helpers/is-bifrost-token";
import useWebViewData from "../../hooks/use-webview-data.js";
import { AUTH_ADDRESS } from "../../constants/api-constants.js";
import { getOrganizationSchemaJson } from "../../constants/organization-schema";
// Removed eager Google Maps and reverse geocoding initialization

export const LoginContext = React.createContext({
    setOnLoginSuccessCallback: () => {},
    setOnLoginCloseCallback: () => {}
});

const AUConfiguration = ({
    children,
    getRefreshTokenInfoConnect,
    logoutSuccessConnect,
    setSecureTokenConnect,
    setGAIdConnect,
    type,
    seoMenu = {},
    getSeoListConnect,
    setCurrentScreenConnect,
    email,
    isLoggedIn,
    abExperiment,
    gaId,
    msiteBi2ph,
    preApprovalOnFlyDesktop,
    preApprovalOnFlyMsite,
    setStatsigInitializationConnect,
    updateIsFirstUserSessionConnect = () => {},
    setModalContentSeenConnect = () => {},
    updateSupportWidgetConfigConnect = () => {},
    updateNudgeCountSyncStatusConnect = () => {},
    setRecentlyViewedDataConnect = () => {},
    fetchSearchResultConnect = () => {},
    verifySignInConnect = () => {},
    statsigConfig = {},
    setBifrostTokensConnect,
    setProfileWithTokenConnect,
    setPrimaryIdentifierTypeConnect,
    openSecondaryIdentifierModalConnect,
    mobile,
    isUmsProfileFetched,
    primaryIdentifierType,
    umsUserId,
    profileName
    // setLocationSuccessConnect = () => {}
    // removed reload car handlers; location updates are handled lazily in selector
}) => {
    const [onLoginSuccessCallback, setOnLoginSuccessCallback] = useState(() => () => {});
    useBifrostSsoCallback({ setBifrostTokensConnect, setProfileWithTokenConnect, setPrimaryIdentifierTypeConnect });
    const isIdleLoaded = useOnloadIdle();
    const [onLoginCloseCallback, setOnLoginCloseCallback] = useState(() => () => {});

    useEffect(() => {
        if (!isLoggedIn) {
            setOnLoginSuccessCallback(() => () => {});
            setOnLoginCloseCallback(() => () => {});
        }
    }, [isLoggedIn]);

    // After login, show secondary identifier modal if phone, email, or name is missing
    useEffect(() => {
        if (!isLoggedIn || !isUmsProfileFetched) return;
        const pending = window.sessionStorage.getItem("bifrost_sso_pending_secondary");
        window.console.log("[DEBUG] au.configuration useEffect", { isLoggedIn, isUmsProfileFetched, pending, mobile, email, profileName });
        if (!pending) return;
        window.sessionStorage.removeItem("bifrost_sso_pending_secondary");
        if (!primaryIdentifierType) {
            setPrimaryIdentifierTypeConnect(mobile ? "phone" : "email");
        }
        if (!mobile || !email || !profileName) {
            window.console.log("[DEBUG] au.configuration: opening SecondaryIdentifierModal");
            openSecondaryIdentifierModalConnect({ skippable: false });
        }
    }, [isLoggedIn, isUmsProfileFetched, primaryIdentifierType, mobile, email, profileName, setPrimaryIdentifierTypeConnect, openSecondaryIdentifierModalConnect]);

    const setOnLoginSuccessCallbackHandler = useCallback((func) => {
        setOnLoginSuccessCallback(() => func);
    }, []);

    const { statsigId } = abExperiment || {};

    const setOnLoginCloseCallbackHandler = useCallback((func) => {
        setOnLoginCloseCallback(() => func);
    }, []);
    const { pathname, search } = useLocation();
    const { resetStoreWithWebViewData } = useWebViewData(search);

    const showSupportWidget = isWebView()
        ? false
        : !SUPPORT_WIDGET_HIDDEN_ROUTES.find((route) => pathname.indexOf(route) > -1);
    const isSellCarPage = C2B_ROUTES.find((route) => pathname.indexOf(route) > -1);

    /*
        initializes event handler class
    */
    useEffect(() => {
        const initateStatsig = async () => {
            await statsigInitialization({ statsigId });
            setStatsigInitializationConnect({ statsigInitialized: true });
        };
        if (!statsigConfig.initialized) {
            initateStatsig();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statsigConfig.initialized, statsigId]);
    useEffect(() => {
        const eventInstance = EventHandler.getInstance();
        window.EventHandler = eventInstance;
        // Set isFirstUserSession to true if the user has not visited the website before
        const hasVisited = getItem(LOCAL_STORAGE_KEYS.hasVisited);
        if (!hasVisited) {
            setItem(true, LOCAL_STORAGE_KEYS.hasVisited);
            updateIsFirstUserSessionConnect(true);
        }
        const modalContentSeen = getItem(LOCAL_STORAGE_KEYS.nudgeModalContentSeen);
        if (modalContentSeen) {
            setModalContentSeenConnect(true);
        }
        // pre bi assistance nudges fire once per category per session
        const categoryNudgeCount = getItemFromSession(SESSION_KEYS.CATEGORY_NUDGE_COUNT);
        if (!categoryNudgeCount) {
            setItemInSession(SESSION_KEYS.CATEGORY_NUDGE_COUNT, {
                [PRE_BI_ASSISTANCE_CATEGORIES.BUYER]: 0,
                [PRE_BI_ASSISTANCE_CATEGORIES.FINANCE]: 0,
                [PRE_BI_ASSISTANCE_CATEGORIES.GENERIC]: 0,
                [PRE_BI_ASSISTANCE_CATEGORIES.SELL]: 0
            });
            updateNudgeCountSyncStatusConnect(true);
        } else {
            updateSupportWidgetConfigConnect({ categoryNudgeCounts: categoryNudgeCount });
            updateNudgeCountSyncStatusConnect(true);
        }

        const { isWebView: queryWebView } = parseLocationSearchParams(search) || {};

        if (queryWebView) {
            resetStoreWithWebViewData();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const shouldHideWidget = SUPPORT_WIDGET_HIDDEN_ROUTES.find((route) => pathname.indexOf(route) > -1);
        const containerExists = document.getElementById("hubspot-messages-iframe-container");

        if (containerExists && shouldHideWidget) {
            containerExists.style.visibility = shouldHideWidget ? "hidden" : "visible";
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    useEffect(() => {
        saveCookie(ISLOGGEDIN_COOKIE, false);
        loadClevertap();
    }, []);

    useEffect(() => {
        window.clevertap = window.clevertap || {};
        window.dataLayer = window.dataLayer || [];
    }, []);

    useEffect(() => {
        window.setTimeout(() => {
            if (window.ga && process.env.GA_ID) {
                window.ga("create", process.env.GA_ID, "auto");
            }
        }, NUMBER.FIVE_HUNDRED);
    }, []);

    useEffect(() => {
        let screenName = getItem("home_screen");
        window.setTimeout(() => {
            if (pathname === LANDING_ROUTE_CONSTANTS.FINANCEYOURCAR) {
                screenName = LANDING_ROUTE_CONSTANTS.FINANCEYOURCAR;
            } else if (pathname === LANDING_ROUTE_CONSTANTS.SELLYOURCAR) {
                screenName = LANDING_ROUTE_CONSTANTS.SELLYOURCAR;
            } else {
                screenName =
                    pathname === LANDING_ROUTE_CONSTANTS.DEFAULT
                        ? LANDING_ROUTE_CONSTANTS.DEFAULT
                        : screenName || LANDING_ROUTE_CONSTANTS.DEFAULT;
            }
            setItem(screenName, "home_screen");
            setCurrentScreenConnect(screenName);
        }, NUMBER.FIVE_HUNDRED);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setCurrentScreenConnect]);

    useEffect(() => {
        if (!seoMenu.isSSR) {
            getSeoListConnect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getSeoListConnect]);

    // clevertap Properties
    useEffect(() => {
        scheduleIdle(() => {
            let cleverTapData = {};
            cleverTapData = { ...cleverTapData, clientId: gaId || "test_value" };
            clevertapUserProperties(cleverTapData);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        window.setTimeout(() => {
            if (window.ga) {
                window.ga((tracker) => {
                    const clientId = tracker.get("clientId");
                    setGAIdConnect(clientId);
                    const TRACKING_CONSENT = emitEventNames.desktop.TRACKING_CONSENT;
                    const trackingConsentValue = JSON.parse(parseCookie(TRACKING_CONSENT));
                    if (!trackingConsentValue) {
                        const clientIdObj = { clientId, allowCookies: null };
                        saveCookie(TRACKING_CONSENT, JSON.stringify(clientIdObj), NUMBER.THIRTY);
                    }
                });
            }
        }, NUMBER.THOUSAND);
    }, [setGAIdConnect]);

    useEffect(() => {
        // In a webview carrying a Bifrost token, the native app owns auth and the
        // session is established from the URL token (UMS). Skip the legacy
        // refresh-token init so it doesn't call the old auth-service /user/me.
        const { isWebView: queryWebView, token: queryToken = "" } = parseLocationSearchParams(search) || {};
        if (queryWebView && isBifrostToken(queryToken)) {
            return;
        }
        getRefreshTokenInfoConnect().catch(() => {});
    }, [getRefreshTokenInfoConnect, search]);

    useEffect(() => {
        const unsubscribe = subscribe(proxyAuthState, () => {
            if (proxyAuthState.isLoggedIn) {
                setSecureTokenConnect(proxyAuthState.accessToken);
            } else {
                logoutSuccessConnect();
            }
        });

        // Unsubscribe by calling the result
        return () => {
            unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proxyAuthState]);

    useEffect(() => {
        if (isLoggedIn) {
            scheduleIdle(() => {
                window.dataLayer.push({ au_email: email });
            });
        }
    }, [email, isLoggedIn]);

    // Map UMS user_id to GA (Universal Analytics) and GTM dataLayer
    useEffect(() => {
        if (isLoggedIn && umsUserId) {
            window.__ums_user_id = umsUserId;
            // Set user_id in Universal Analytics directly
            if (window.ga) {
                window.ga("set", "userId", umsUserId);
            }
            // Push to GTM dataLayer for GA4 tag mapping
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ user_id: umsUserId });
        }
        if (!isLoggedIn && !umsUserId) {
            window.__ums_user_id = "";
            // Clear user_id on logout
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ user_id: undefined });
        }
    }, [isLoggedIn, umsUserId]);

    useEffect(() => {
        if (isIdleLoaded) {
            scheduleIdle(() => {
                const deviceType = getDeviceType();
                const isBi2phVariant = getAbExpirementVariant(msiteBi2ph, EXPERIMENT_TYPE.VARIANT_B);
                if (deviceType === DEVICE_TYPE.MOBILE) {
                    trackCommonCustomEventsAU(ANALYTICS_EVENTS.BI_TO_PHONE_EXPOSURE_EVENT, {
                        ...ANALYTICS_EVENTS.BI_TO_PHONE_EXPOSURE_EVENT,
                        eventLabel: isBi2phVariant ? EXPERIMENT_TYPE.VARIANT_B : EXPERIMENT_TYPE.VARIANT_A
                    });
                }
            });
        }
    }, [isIdleLoaded, msiteBi2ph]);

    useEffect(() => {
        if (isIdleLoaded) {
            const deviceType = getDeviceType();
            const experiment = deviceType === DEVICE_TYPE.MOBILE ? preApprovalOnFlyMsite : preApprovalOnFlyDesktop;
            const isPreApprovalOnFlyVariant = getAbExpirementVariant(experiment, EXPERIMENT_TYPE.VARIANT_B);
            trackCommonCustomEventsAU("custom_event", {
                event: "experiment",
                eventCategory: "pre_approval_on_fly",
                eventAction: "variant_assigned",
                eventLabel: isPreApprovalOnFlyVariant ? "VARIANT_B" : "VARIANT_A"
            });
        }
    }, [isIdleLoaded, preApprovalOnFlyDesktop, preApprovalOnFlyMsite]);

    useExperiments({ experimentKeys: [
        EXPERIMENT_NAME.financeFirstCheckoutRevamp,
        EXPERIMENT_NAME.desktop_handover_charges,
        EXPERIMENT_NAME.msite_handover_charges,
        EXPERIMENT_NAME.msite_single_page_td,
        EXPERIMENT_NAME.msite_chr_widget,
        EXPERIMENT_NAME.desktop_chr_widget
    ]});

    const { handleThirdPartySignIn } = useThirdPartySignIn({
        onLoginSuccessCallback,
        setRecentlyViewedDataConnect,
        fetchSearchResultConnect
    });

    const handleGoogleSuccess = useCallback(
        (payload) => {
            handleThirdPartySignIn(payload, AUTH_ADDRESS.GOOGLE);
        },
        [handleThirdPartySignIn]
    );

    const handleGoogleError = useCallback(() => {
        // console.log("=>Google One Tap error", error);
    }, []);

    useGoogleOneTap({
        onSuccess: handleGoogleSuccess,
        onError: handleGoogleError,
        verifySignInConnect,
        type,
        isIdleLoaded
    });
    const SupportWidget = type === "desktop" ? SupportWidgetDesktop : SupportWidgetMobile;
    return (
        <Fragment>
            <Helmet
                script={[
                    {
                        type: "text/javascript",
                        innerHTML: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.defer=true;j.src=
                  '//www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                  })(window,document,'script','dataLayer','${process.env.GTM_ID_AU}');`
                    },
                    {
                        type: "application/ld+json",
                        innerHTML: getOrganizationSchemaJson()
                    }
                ]}
                meta={[
                    { name: "google-site-verification", content: "VUY_EcsQTevkNtbtQbpgkZrJoLQE4L1OincJrfjbJcw" },
                    { name: "google-site-verification", content: "gydA6QDX5Z1TF1TjzhdI7BWR2mbtO8cuw2t-vPkUrHI" }
                ]}
            />
            <LoginContext.Provider
                value={{
                    setOnLoginSuccessCallback: setOnLoginSuccessCallbackHandler,
                    setOnLoginCloseCallback: setOnLoginCloseCallbackHandler
                }}
            >
                {React.Children.map(children, (child) => {
                    return React.cloneElement(child, {
                        onLoginSuccessCallback,
                        setOnLoginSuccessCallback: setOnLoginSuccessCallbackHandler,
                        onLoginCloseCallback,
                        setOnLoginCloseCallback: setOnLoginCloseCallbackHandler,
                        type
                    });
                })}
                {showSupportWidget && isIdleLoaded && (
                    <ErrorHandler>{isIdleLoaded && <SupportWidget isSellCarPage={isSellCarPage} />}</ErrorHandler>
                )}
            </LoginContext.Provider>
        </Fragment>
    );
};

AUConfiguration.propTypes = {
    children: PropTypes.any.isRequired,
    getRefreshTokenInfoConnect: PropTypes.any.isRequired,
    logoutSuccessConnect: PropTypes.func,
    setSecureTokenConnect: PropTypes.func,
    setGAIdConnect: PropTypes.func,
    type: PropTypes.string,
    getSeoListConnect: PropTypes.func,
    seoMenu: PropTypes.object,
    setCurrentScreenConnect: PropTypes.func,
    email: PropTypes.string,
    isLoggedIn: PropTypes.bool,
    abExperiment: PropTypes.object,
    financeOpted: PropTypes.bool,
    financeProvider: PropTypes.string,
    msiteBi2ph: PropTypes.object,
    preApprovalOnFlyDesktop: PropTypes.object,
    preApprovalOnFlyMsite: PropTypes.object,
    gaId: PropTypes.func,
    updateIsFirstUserSessionConnect: PropTypes.func,
    updateSupportWidgetConfigConnect: PropTypes.func,
    setModalContentSeenConnect: PropTypes.func,
    updateNudgeCountSyncStatusConnect: PropTypes.func,
    setStatsigInitializationConnect: PropTypes.func,
    setRecentlyViewedDataConnect: PropTypes.func,
    fetchSearchResultConnect: PropTypes.func,
    verifySignInConnect: PropTypes.func,
    statsigConfig: PropTypes.object,
    reloadCarListMobileConnect: PropTypes.func,
    reloadCarListDesktopConnect: PropTypes.func,
    setLocationSuccessConnect: PropTypes.func,
    setBifrostTokensConnect: PropTypes.func,
    setProfileWithTokenConnect: PropTypes.func,
    setPrimaryIdentifierTypeConnect: PropTypes.func,
    openSecondaryIdentifierModalConnect: PropTypes.func,
    mobile: PropTypes.string,
    isUmsProfileFetched: PropTypes.bool,
    primaryIdentifierType: PropTypes.string,
    umsUserId: PropTypes.string,
    profileName: PropTypes.string
};

export default AUConfiguration;
